import type { RxCollection } from 'rxdb';
import { findTimeEntryOverlaps, type TimeEntryDocType } from '@ast-data/shared';

/**
 * Entries across ALL projects that overlap `candidate` — the same day's synced
 * rows (queried from RxDB) plus any local extras. The candidate is excluded by
 * id. Empty when it has no date yet.
 *
 * This is the pre-save (best-effort) check: it can only see what is on the
 * device, so a truly concurrent offline double-booking is caught later by the
 * post-sync flag (useTimeEntryOverlaps).
 */
export async function queryTimeEntryOverlaps(
    collection: RxCollection<TimeEntryDocType> | null,
    candidate: TimeEntryDocType,
    extra: TimeEntryDocType[] = []
): Promise<TimeEntryDocType[]> {
    if (!candidate.date) {
        return [];
    }
    // Only the candidate's own variant can conflict with it (a Gerät and a
    // person who share a name are not one another), so the query is narrowed
    // rather than leaving it to findTimeEntryOverlaps to reject.
    const sameDay = collection
        ? (
              await collection
                  .find({
                      selector: {
                          date: candidate.date,
                          entryType: candidate.entryType
                      }
                  })
                  .exec()
          ).map((doc) => doc.toJSON() as TimeEntryDocType)
        : [];
    return findTimeEntryOverlaps(candidate, [...sameDay, ...extra]);
}
